import {
  SelectContent,
  SelectField,
  SelectFieldProps,
  SelectItem,
} from "@/components/atoms/Select";
import TimeIcon from "@/icons/__generated__/Time/Time20";
import { BookingScheduleTime } from "@/lib/types/schedule-types";
import { cn } from "@/lib/utils";
import { useFormContext } from "react-hook-form";
import { normalizeTimeData, TimeData } from "./time-utils";
import { TimeSelectProps } from "./TimeSelect";

const defaultStartTimeProps = {
  placeholder: "Select start time",
  name: "startTime",
};

const defaultEndTimeProps = {
  placeholder: "Select end time",
  name: "endTime",
};

export type TimeSelectFieldProps = Omit<
  TimeSelectProps,
  "startTimeProps" | "endTimeProps" | "ref" | "value" | "onValueChange"
> & {
  startTimeProps?: Partial<Omit<SelectFieldProps, "renderContent">>;
  endTimeProps?: Partial<Omit<SelectFieldProps, "renderContent">>;
  displayTimeIcon?: boolean;
  onValueChange?: (
    fieldName: string,
    timeObj: BookingScheduleTime | null,
  ) => void;
};

export const TimeSelectField = ({
  startTimeProps,
  endTimeProps,
  showEndTime = true,
  fullWidth = true,
  className,
  disabled,
  testId,
  timeOptions,
  onValueChange,
  displayTimeIcon = true,
  ...containerProps
}: TimeSelectFieldProps) => {
  const { watch, setValue } = useFormContext();

  const finalStartTimeProps = {
    ...defaultStartTimeProps,
    ...(startTimeProps || {}),
  };

  const finalEndTimeProps = {
    ...defaultEndTimeProps,
    ...(endTimeProps || {}),
  };

  const startTimeOptions = timeOptions?.start
    ? normalizeTimeData(timeOptions.start || [])
    : TimeData;

  const endTimeOptions = timeOptions?.end
    ? normalizeTimeData(timeOptions.end || [])
    : TimeData;

  const selectedStartTime = watch(finalStartTimeProps.name);
  const selectedEndTime = watch(finalEndTimeProps.name);

  return (
    <div
      className={cn(
        "tw-flex",
        {
          "tw-w-full": fullWidth,
        },
        className,
      )}
      {...containerProps}
    >
      <SelectField
        fullWidth={true}
        prefix={displayTimeIcon ? <TimeIcon /> : null}
        testId={testId ? `${testId}-start-time` : undefined}
        value={String(selectedStartTime?.time_index ?? "")}
        onValueChange={(newStartTimeIndex) => {
          // clear end time if start time is greater than end time
          const endTimeIndex = selectedEndTime?.time_index || -1;

          if (newStartTimeIndex >= endTimeIndex) {
            setValue(finalEndTimeProps.name, null);
            onValueChange?.(finalEndTimeProps.name, null);
          }

          const newSelectedStartTime = (startTimeOptions || []).find(
            (timeObj) => timeObj.time_index === parseInt(newStartTimeIndex),
          );
          setValue(finalStartTimeProps.name, newSelectedStartTime);
          onValueChange?.(finalStartTimeProps.name, newSelectedStartTime!);
        }}
        renderContent={() => (
          <SelectContent>
            {(startTimeOptions || []).map((option: BookingScheduleTime) => {
              return (
                <SelectItem
                  key={option.time_index}
                  value={String(option.time_index)}
                  disabled={!option.available && option.available !== undefined}
                >
                  {option.time}
                </SelectItem>
              );
            })}
          </SelectContent>
        )}
        {...finalStartTimeProps}
        className={cn(
          "tw-flex-1",
          {
            "tw-max-w-[50%] tw-pr-8": showEndTime,
          },
          finalStartTimeProps.className,
        )}
        disabled={disabled || finalStartTimeProps.disabled}
      />

      {showEndTime && (
        <SelectField
          fullWidth={true}
          testId={testId ? `${testId}-end-time` : undefined}
          value={String(selectedEndTime?.time_index ?? "")}
          prefix={displayTimeIcon ? <TimeIcon /> : null}
          onValueChange={(timeIndex) => {
            const newSelectedEndTime = (endTimeOptions || []).find(
              (timeObj) => timeObj.time_index === parseInt(timeIndex),
            );

            setValue(finalEndTimeProps.name, newSelectedEndTime);
            onValueChange?.(finalEndTimeProps.name, newSelectedEndTime!);
          }}
          renderContent={() => (
            <SelectContent>
              {(endTimeOptions || []).map((option: BookingScheduleTime) => {
                return (
                  <SelectItem
                    key={option.time_index}
                    value={String(option.time_index)}
                    disabled={
                      !option.available && option.available !== undefined
                    }
                  >
                    {option.time}
                  </SelectItem>
                );
              })}
            </SelectContent>
          )}
          {...finalEndTimeProps}
          className={cn(
            "tw-flex-1 tw-max-w-[50%] tw-pl-8",
            finalEndTimeProps.className,
          )}
          disabled={
            disabled ||
            finalEndTimeProps.disabled ||
            !selectedStartTime ||
            (!selectedStartTime.available &&
              selectedStartTime.available !== undefined)
          }
        />
      )}
    </div>
  );
};
